
import React, { useState, useEffect, useRef } from 'react';
import { Separator } from '@/components/ui/separator';
import { useAiAssistant } from '@/contexts/AiAssistantContext';
import { TopicType, SessionType } from '@/types/assistant';
import { useSidebar } from '@/contexts/SidebarContext';
import { motion, AnimatePresence } from 'framer-motion';
import { RenameSessionDialog } from '../dialogs/RenameSessionDialog';
import { DeleteSessionDialog } from '../dialogs/DeleteSessionDialog';
import { useIsMobile } from '@/hooks/use-mobile';
import { SidebarUsageDisplay } from './SidebarUsageDisplay';
import { SidebarHeader } from './sidebar/SidebarHeader';
import { SidebarSearch } from './sidebar/SidebarSearch';
import { SidebarFilters } from './sidebar/SidebarFilters';
import { SidebarSessionsList } from './sidebar/SidebarSessionsList';

export const AiSidebar = () => {
  const { 
    sessions, 
    currentSession, 
    createNewSession, 
    selectSession,
    searchSessions,
    messages
  } = useAiAssistant();
  
  const { isCollapsed } = useSidebar();
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredSessions, setFilteredSessions] = useState(sessions);
  const [activeTopic, setActiveTopic] = useState<TopicType | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  // State for dialog controls
  const [sessionToRename, setSessionToRename] = useState<SessionType | null>(null);
  const [sessionToDelete, setSessionToDelete] = useState<SessionType | null>(null);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (currentSession) {
      setActiveTopic(currentSession.topic);
    }
  }, [currentSession]);

  useEffect(() => {
    let results = sessions;
    
    // Apply search filter
    if (searchQuery.trim()) {
      results = searchSessions(searchQuery);
    }
    
    // Apply topic filter
    if (activeTopic) {
      results = results.filter(session => session.topic === activeTopic);
    }
    
    // Sort by most recent
    results = [...results].sort((a, b) => b.updated_at.getTime() - a.updated_at.getTime());
    
    setFilteredSessions(results);
  }, [searchQuery, activeTopic, sessions, searchSessions]);

  const handleNewSession = (topic: TopicType) => {
    createNewSession(topic);
    setActiveTopic(topic);
    setSearchQuery("");
  };

  const handleSearchFocus = () => {
    // Clear topic filter when focusing on search
    setActiveTopic(null);
  };

  const handleTopicClick = (topic: TopicType | null) => {
    setActiveTopic(topic);
    setSearchQuery("");
  };

  const handleRenameClick = (e: React.MouseEvent, session: SessionType) => {
    e.stopPropagation();
    setSessionToRename(session);
    setIsRenameDialogOpen(true);
  };

  const handleDeleteClick = (e: React.MouseEvent, session: SessionType) => {
    e.stopPropagation();
    setSessionToDelete(session);
    setIsDeleteDialogOpen(true);
  };

  // Optimized animation variants for better mobile performance
  const sidebarVariants = {
    expanded: { 
      width: 320, 
      opacity: 1, 
      transition: { 
        duration: 0.2, 
        ease: [0.4, 0, 0.2, 1], // Custom easing for smoother animation
        width: { duration: 0.2 },
        opacity: { duration: 0.15, delay: 0.05 }
      } 
    },
    collapsed: { 
      width: 0, 
      opacity: 0, 
      transition: { 
        duration: 0.2, 
        ease: [0.4, 0, 0.2, 1],
        opacity: { duration: 0.1 },
        width: { duration: 0.2, delay: 0.1 }
      } 
    }
  };

  // Simplified content variants - remove staggerChildren for better performance
  const contentVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.15 }
    }
  };

  return (
    <motion.div 
      className="border-r border-white/10 bg-background/20 backdrop-blur-xl flex flex-col relative overflow-hidden"
      initial="collapsed"
      animate={isCollapsed ? "collapsed" : "expanded"}
      variants={sidebarVariants}
      style={{
        height: "calc(100dvh - env(safe-area-inset-top))",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        // Use transform3d for hardware acceleration
        backfaceVisibility: 'hidden',
        perspective: 1000,
        willChange: 'transform, opacity, width'
      }}
    >
      <AnimatePresence mode="wait">
        {!isCollapsed && (
          <motion.div 
            className="flex flex-col h-full"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0 }}
            style={{
              // Hardware acceleration for smoother animations
              transform: 'translateZ(0)'
            }}
          >
            <div className="p-4 space-y-4">
              <SidebarHeader 
                onNewSession={handleNewSession}
              />

              <SidebarSearch
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onSearchFocus={handleSearchFocus}
                searchInputRef={searchInputRef}
              />

              <SidebarFilters
                activeTopic={activeTopic}
                onTopicClick={handleTopicClick}
              />
            </div>

            <Separator className="bg-white/10" />

            <SidebarSessionsList
              filteredSessions={filteredSessions}
              currentSession={currentSession}
              messages={messages}
              searchQuery={searchQuery}
              onSessionClick={selectSession}
              onRenameClick={handleRenameClick}
              onDeleteClick={handleDeleteClick}
            />

            {/* Usage Display at Bottom */}
            {currentSession && (
              <div className="p-4 pt-2">
                <SidebarUsageDisplay topic={currentSession.topic} />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dialogs */}
      {sessionToRename && (
        <RenameSessionDialog
          sessionId={sessionToRename.id}
          currentName={sessionToRename.name_session}
          isOpen={isRenameDialogOpen}
          onOpenChange={(open) => {
            setIsRenameDialogOpen(open);
            if (!open) setSessionToRename(null);
          }}
        />
      )}

      {sessionToDelete && (
        <DeleteSessionDialog
          sessionId={sessionToDelete.id}
          sessionName={sessionToDelete.name_session}
          isOpen={isDeleteDialogOpen}
          onOpenChange={(open) => {
            setIsDeleteDialogOpen(open);
            if (!open) setSessionToDelete(null);
          }}
        />
      )}
    </motion.div>
  );
};
